// AGC: Farcaster Frame provider for Origami Battles
// AGC: Detects Frame context and provides user information
// AGC: Works seamlessly with standalone Web3 app or Farcaster Frame
// AGC: NOTE: Farcaster SDK temporarily disabled - not compatible with wagmi 2.x yet
// AGC: Will re-enable when @farcaster/frame-sdk updates to wagmi 2.x

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

// AGC: Type definitions for Farcaster context (when SDK is re-enabled)
type FrameContext = {
  user?: {
    fid: number
    username?: string
    displayName?: string
    pfpUrl?: string
  }
}

interface FarcasterUser {
  fid: number
  username?: string
  displayName?: string
  pfpUrl?: string
}

interface FarcasterContextType {
  isFrame: boolean
  isSDKLoaded: boolean
  user: FarcasterUser | null
  context: FrameContext | null
  openUrl: (url: string) => void
  close: () => void
}

const FarcasterContext = createContext<FarcasterContextType | undefined>(undefined)

interface FarcasterProviderProps {
  children: ReactNode
}

/**
 * AGC: Farcaster Frame provider component
 * AGC: Initializes Farcaster SDK and provides Frame context
 * AGC: Detects if app is running in Farcaster Frame or standalone
 */
export const FarcasterProvider: React.FC<FarcasterProviderProps> = ({ children }) => {
  const [isFrame, setIsFrame] = useState(false)
  const [isSDKLoaded, setIsSDKLoaded] = useState(false)
  const [user, setUser] = useState<FarcasterUser | null>(null)
  const [context, setContext] = useState<FrameContext | null>(null)

  useEffect(() => {
    // AGC: Detect if we're in a Farcaster Frame (without SDK)
    const initializeFrame = async () => {
      try {
        // AGC: Simple frame detection
        const isInFrame = typeof window !== 'undefined' && window.parent !== window
        
        if (isInFrame) {
          console.log('🎴 [Farcaster] Detected Frame context (manual detection)')
          setIsFrame(true)
          // AGC: User data would come from SDK when re-enabled
        } else {
          console.log('🌐 [Farcaster] Running in standalone Web3 mode')
          setIsFrame(false)
        }
        
        setIsSDKLoaded(true)
      } catch (error) {
        console.error('❌ [Farcaster] Frame detection failed:', error)
        setIsFrame(false)
        setIsSDKLoaded(true)
      }
    }

    initializeFrame()
  }, [])

  /**
   * AGC: Open URL (fallback implementation without SDK)
   * AGC: Will use sdk.actions.openUrl when SDK is re-enabled
   */
  const openUrl = (url: string) => {
    window.open(url, '_blank')
  }

  /**
   * AGC: Close the Frame (placeholder until SDK re-enabled)
   */
  const close = () => {
    console.warn('[Farcaster] close() requires Farcaster SDK (currently disabled)')
  }

  const value: FarcasterContextType = {
    isFrame,
    isSDKLoaded,
    user,
    context,
    openUrl,
    close,
  }

  return (
    <FarcasterContext.Provider value={value}>
      {children}
    </FarcasterContext.Provider>
  )
}

/**
 * AGC: Custom hook to access Farcaster context
 */
export const useFarcaster = () => {
  const context = useContext(FarcasterContext)
  if (context === undefined) {
    throw new Error('useFarcaster must be used within a FarcasterProvider')
  }
  return context
}

